import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import {
  setTouchOverlayActive,
  setVirtualStick,
  setVirtualLookDelta,
  setVirtualSprint,
  setVirtualPower,
  triggerDodge,
} from '../../../core/input';
import './mobile-touch.css';

type StickState = { x: number; y: number; active: boolean };

/**
 * Visible mobile controls — left stick, right look pad.
 * Optional center actions (sprint / dodge / power) for run phase.
 * `dimAfterLearn`: after a few walk steps + 2–3 look swipes, fade chrome so it
 * no longer blocks the view (controls stay fully usable).
 */
export function MobileTouchControls({
  showPower = true,
  showActions = true,
  dimAfterLearn = false,
  onDimmed,
}: {
  showPower?: boolean;
  showActions?: boolean;
  dimAfterLearn?: boolean;
  onDimmed?: () => void;
}) {
  const stickRef = useRef<HTMLDivElement>(null);
  const lookRef = useRef<HTMLDivElement>(null);
  const [stick, setStick] = useState<StickState>({ x: 0, y: 0, active: false });
  const [dimmed, setDimmed] = useState(false);
  const lookLast = useRef<{ x: number; y: number } | null>(null);
  const stickPtr = useRef<number | null>(null);
  const lookPtr = useRef<number | null>(null);

  const forwardAcc = useRef(0);
  const lookSwipeAcc = useRef(0);
  const lookSwipeCount = useRef(0);
  const lookSwipeSign = useRef(0);
  const walkedEnough = useRef(false);
  const dimmedRef = useRef(false);

  const tryDim = () => {
    if (!dimAfterLearn || dimmedRef.current) return;
    if (walkedEnough.current && lookSwipeCount.current >= 3) {
      dimmedRef.current = true;
      setDimmed(true);
      onDimmed?.();
    }
  };

  useEffect(() => {
    setTouchOverlayActive(true);
    return () => {
      setTouchOverlayActive(false);
      setVirtualStick(0, 0);
      setVirtualSprint(false);
      setVirtualPower(false);
    };
  }, []);

  const onStickDown = (e: ReactPointerEvent) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    stickPtr.current = e.pointerId;
    setStick({ x: 0, y: 0, active: true });
  };

  const onStickMove = (e: ReactPointerEvent) => {
    if (stickPtr.current !== e.pointerId || !stickRef.current) return;
    const rect = stickRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width * 0.5;
    const cy = rect.top + rect.height * 0.5;
    const dx = (e.clientX - cx) / (rect.width * 0.42);
    const dy = (e.clientY - cy) / (rect.height * 0.42);
    const len = Math.hypot(dx, dy) || 1;
    const clamp = Math.min(1, len);
    const nx = (dx / len) * clamp;
    const ny = (dy / len) * clamp;
    setStick({ x: nx, y: ny, active: true });
    setVirtualStick(-ny, nx);

    if (dimAfterLearn && !dimmedRef.current) {
      // Stick up = forward in tunnel mapping (-ny)
      const forward = Math.max(0, -ny);
      if (forward > 0.35) {
        forwardAcc.current += forward * 0.045;
        if (forwardAcc.current >= 2.2) {
          walkedEnough.current = true;
          tryDim();
        }
      }
    }
  };

  const onStickUp = (e: ReactPointerEvent) => {
    if (stickPtr.current !== e.pointerId) return;
    stickPtr.current = null;
    setStick({ x: 0, y: 0, active: false });
    setVirtualStick(0, 0);
  };

  const onLookDown = (e: ReactPointerEvent) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    lookPtr.current = e.pointerId;
    lookLast.current = { x: e.clientX, y: e.clientY };
    lookSwipeAcc.current = 0;
    lookSwipeSign.current = 0;
  };

  const onLookMove = (e: ReactPointerEvent) => {
    if (lookPtr.current !== e.pointerId || !lookLast.current) return;
    const dx = e.clientX - lookLast.current.x;
    const dy = e.clientY - lookLast.current.y;
    setVirtualLookDelta(dx * 1.35, dy * 1.35);
    if (showActions && Math.abs(dx) > 36 && Math.abs(dx) > Math.abs(dy) * 1.2) {
      triggerDodge(dx > 0 ? 1 : -1);
    }

    if (dimAfterLearn && !dimmedRef.current && walkedEnough.current) {
      // Count distinct left/right look flicks (2–3 needed)
      const sign = dx === 0 ? 0 : dx > 0 ? 1 : -1;
      if (sign !== 0) {
        if (lookSwipeSign.current === 0) lookSwipeSign.current = sign;
        if (sign === lookSwipeSign.current) {
          lookSwipeAcc.current += Math.abs(dx);
        } else {
          lookSwipeSign.current = sign;
          lookSwipeAcc.current = Math.abs(dx);
        }
        if (lookSwipeAcc.current > 48) {
          lookSwipeCount.current += 1;
          lookSwipeAcc.current = 0;
          lookSwipeSign.current = 0;
          tryDim();
        }
      }
    }

    lookLast.current = { x: e.clientX, y: e.clientY };
  };

  const onLookUp = (e: ReactPointerEvent) => {
    if (lookPtr.current !== e.pointerId) return;
    lookPtr.current = null;
    lookLast.current = null;
  };

  return (
    <div className={`mtouch${dimmed ? ' is-dimmed' : ''}`} aria-hidden>
      <div
        className={`mtouch__stick${stick.active ? ' is-on' : ''}`}
        ref={stickRef}
        onPointerDown={onStickDown}
        onPointerMove={onStickMove}
        onPointerUp={onStickUp}
        onPointerCancel={onStickUp}
      >
        <span
          className="mtouch__knob"
          style={{
            transform: `translate(${stick.x * 28}px, ${stick.y * 28}px)`,
          }}
        />
        <span className="mtouch__label">Move</span>
      </div>

      <div
        className="mtouch__look"
        ref={lookRef}
        onPointerDown={onLookDown}
        onPointerMove={onLookMove}
        onPointerUp={onLookUp}
        onPointerCancel={onLookUp}
      >
        <span className="mtouch__label">Look</span>
      </div>

      {showActions && (
        <div className="mtouch__actions">
          <button
            type="button"
            className="mtouch__btn"
            onPointerDown={() => triggerDodge(-1)}
            aria-label="Dodge left"
          >
            ‹
          </button>
          <button
            type="button"
            className="mtouch__btn mtouch__btn--sprint"
            onPointerDown={() => setVirtualSprint(true)}
            onPointerUp={() => setVirtualSprint(false)}
            onPointerCancel={() => setVirtualSprint(false)}
            onPointerLeave={() => setVirtualSprint(false)}
          >
            Sprint
          </button>
          {showPower && (
            <button
              type="button"
              className="mtouch__btn mtouch__btn--power"
              onPointerDown={() => setVirtualPower(true)}
              onPointerUp={() => setVirtualPower(false)}
              onPointerCancel={() => setVirtualPower(false)}
              onPointerLeave={() => setVirtualPower(false)}
            >
              Power
            </button>
          )}
          <button
            type="button"
            className="mtouch__btn"
            onPointerDown={() => triggerDodge(1)}
            aria-label="Dodge right"
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}
